import React, { useContext } from 'react';
import { useLocalizedStrings } from '../../Localization';
import { PublicConfigurationContext } from '../../Context';
import { useHistory } from 'react-router-dom';
import { Badge, Button, DataTable, Icon, NumberDisplay, Page, SidebarNavTrigger, Tooltip } from '@aidkitorg/component-library';
import { AdminPanelNav } from '../AdminPanelApp';
import { ColumnDef } from '@tanstack/react-table';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { PaymentsFilter } from './PaymentsFilter';
import { Payment } from '@aidkitorg/roboscreener/lib/compute/derived/Payment';

// FAKE PAYMENTS DATA
// TODO: move to API or PaymentsService
export type Payment = {
  batchName: string
  enrolled: number
  eligible: number
  recipients: number
  failed: number
  totalAmount: string
  status: 'approved' | 'pending_approval'
  paymentDate: Date,
}

const paymentColumns: ColumnDef<Payment>[] = [
  {
    accessorKey: "batchName",
    header: "Batch Name",
    cell: ({ row }) => {
      const batchName = row.original.batchName
      const enrolled = row.original.enrolled
      const eligible = row.original.eligible

      const tooltipContent = (<div>
        <div>"Enrolled" are applicants who have provided payment details.</div>
        <div>"Eligible" are applicants who qualify for payment.</div>
      </div>)

      return (
        <div>
          <div className="font-medium text-md">{batchName}</div>
          <div className="flex gap-2 text-xs text-gray-700">
            <div>{enrolled} Enrolled/{eligible} Eligible</div>
            <div>
              <Tooltip asChild text={tooltipContent}>
                <button aria-label="Info">
                  <Icon size="sm" name="InformationCircleIcon" role="decorative"/>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      )
    }
  },
  {
    accessorKey: "recipients",
    header: "Recipients",
    cell: ({row}) => {
      return (<Button variant="link" href="/">{row.original.recipients} recipients</Button>)
    }
  },
  {
    accessorKey: "failed",
    header: "Failed",
  },
  {
    accessorKey: "totalAmount",
    header: "Total Amount",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status
      const variant = status === 'approved' ? 'success' : 'warning'
      const copy = status === 'approved' ? "Approved" : "Pending Approval"
      return (
        <Badge variant={variant} >{copy}</Badge>
      )
    },
  },
  {  
    accessorKey: "paymentDate",
    header: "Payment Date",
    cell: ({ row }) => {
      const date = row.original.paymentDate
      const formatted = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)

      return <span>{formatted}</span>
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.paymentDate.getTime()
      const b = rowB.original.paymentDate.getTime()
      return a - b
    },
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue?.start || !filterValue?.end) return true
      const date = row.getValue(columnId) as Date
      return (
        date >= new Date(filterValue.start) &&
          date <= new Date(filterValue.end)
      )
    },
  }
]

const paymentData: Payment[] = [
  {
    batchName: 'March Housing Assistance',
    enrolled: 55,
    eligible: 89,
    recipients: 25,
    failed: 2,
    totalAmount: "$125,000",
    status: 'pending_approval',
    paymentDate: new Date('2024-03-31'),
  },
  {
    batchName: 'Emergency Rental Support',
    enrolled: 44,
    eligible: 78,
    recipients: 30,
    failed: 0,
    totalAmount: "$75,000",
    status: 'approved',
    paymentDate: new Date('2024-04-14'),
  },
  {
    batchName: 'LA County Wildfire Support',
    enrolled: 90,
    eligible: 143,
    recipients: 198,
    failed: 0,
    totalAmount: "$890,000",
    status: 'approved',
    paymentDate: new Date('2025-01-10'),
  },
  {
    batchName: 'RXKids Payment 1',
    enrolled: 35,
    eligible: 89,
    recipients: 48,
    failed: 0,
    totalAmount: "$39,000",
    status: 'pending_approval',
    paymentDate: new Date('2024-10-14'),
  },
]

const paymentActions: TableAction<Payment>[] = [
  {
    label: "Preview Notification",
    onClick: (row) => alert(`Previewing notifications for ${row.batchName}`),
    icon: 'EyeIcon'
  },
  {
    label: "View Recipients",
    onClick: (row) => alert(`Viewing recipients for ${row.batchName}`),
    icon: 'UserGroupIcon'
  },
]

export const PaymentsPage = () => {
  const L = useLocalizedStrings()
  const publicConfig = useContext(PublicConfigurationContext);
  const history = useHistory()
  
  if (publicConfig && publicConfig.adminPanel === false){
    history.push("/");
  }

  // TODO: make payments context that provides fake payments data to show on page

  // TODO: discuss with Kate and Devin about the design of this card?
  const enabledPaymentsCard = <NumberDisplay
    className="max-w-56 p-4"
    title="Enabled Payments"
    value={125} 
    unit="payments"
    iconName="PlayCircleIcon" 
    iconColor="blue"
  />
  
  const paymentsDataTable = <DataTable 
    columns={paymentColumns} 
    data={paymentData}
    actions={paymentActions}
    enableSearch
    searchLabel="Search payments..."
    filters={(table) => <PaymentsFilter table={table}/>}
  />

  // TODO: consolidate header footer into a AdminPanelShell Page or something
  
  const header = (
    <div>
      <SidebarNavTrigger />
    </div>
  )
  
  const footer = (
    <div className="w-full flex justify-between">
      <div>
        <Button variant="link" href="/privacy.html" openInNewWindow>
          {L.privacy_policy}
        </Button>
      </div>
      <div>
        <Button variant="link" href="/tos.html" openInNewWindow>
          {L.terms_and_conditions}
        </Button>
      </div>
    </div>
  )
  
  return (
    <Page 
      className="bg-gray-50"
      header={header}
      sidebar={
        <AdminPanelNav />
      }
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div>
          {enabledPaymentsCard}
        </div>
        <div>
          <div className="border rounded-md p-4 bg-white">{paymentsDataTable}</div>
        </div>
      </div>
    </Page>
  )
}